import React, { useContext, useMemo } from 'react';
//mui imports====
import { Box } from '@mui/system';
import useStyles from './styles';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { UserContext } from '../../Contexts/UserContext';
import { ACCESS_CONTROL_CONSTANTS } from '../../core/constants';
import InfoButton from '../InfoButton';
//===============

const { SYSTEMS, UI_ACCESS, TYPES } = ACCESS_CONTROL_CONSTANTS;

const InputConfig = ({
	keyData,
	id = 'outlined-size-normal',
	label,
	error = false,
	helperText,
	inputType,
	object,
	updateObject,
	editable,
	inputProps,
	iconConfiguration = {},
	onChangeCheck,
	isMandatory = false,
	containerStyles,
	labelStyles,
	inputStyles,
	isObjectEditor = true,
	directValue,
	updateDirectValue,
	multiline = false,
	rows = 1,
	system = SYSTEMS.FRAMERATE,
	placeholder = '',
	batchUpdateForObjectEditor = [],
	showInfoButton = false,
	infoButtonProps = {},
	infoMessage = '',
	nullable = false
}) => {
	const classes = useStyles();
	const { getAccessControlSet } = useContext(UserContext);
	/**
	 * This variable contains a set of strings that signifies if this input is either visible/disabled/hidden
	 * @type {Set<string>}
	 */
	const inputControlledAccessSet = useMemo(() => {
		return getAccessControlSet(system, TYPES.INPUT_NAMES, keyData);
	}, [getAccessControlSet, keyData, system]);

	const getValue = () => {
		if (isObjectEditor) {
			return object[keyData] ?? '';
		}
		return directValue ?? '';
	};

	const getLabel = () => {
		return isMandatory ? `${label} *` : label;
	};

	const handleChange = (e) => {
		let val = e.target.value;
		if (inputType === 'number') {
			// For nullable number inputs, set to null when empty; otherwise convert to number
			val = val === '' && nullable ? null : Number(val);
		}
		if (editable === false) return;
		if (onChangeCheck) {
			if (!onChangeCheck(val)) val = 100;
		}
		if (isObjectEditor) {
			updateObject((obj) => {
				let deepClonedObj = structuredClone(obj);
				deepClonedObj = { ...deepClonedObj, [keyData]: val };

				//Handle batch key value updates
				batchUpdateForObjectEditor.forEach((batchUpdate) => {
					deepClonedObj[batchUpdate.key] = batchUpdate.value;
				});

				return deepClonedObj;
			});
		} else {
			updateDirectValue(val);
		}
	};

	const textFieldProps = {
		type: inputType,
		id,
		error,
		helperText,
		style: inputStyles || { width: '100%', margin: '1rem 0' },
		value: getValue(),
		disabled: editable === false || inputControlledAccessSet.has(UI_ACCESS.DISABLE),
		onChange: handleChange,
		InputProps: { inputProps, ...iconConfiguration },
		multiline,
		rows,
		placeholder
	};

	// this is to not let scrolling change numbers of the input box
	if (inputType === 'number') {
		textFieldProps.onWheel = (e) => e.target.blur();
	}

	return (
		<Box className={classes.inputConfigContainer} style={containerStyles || {}}>
			{!inputControlledAccessSet.has(UI_ACCESS.HIDE) && (
				<>
					<Typography
						className={classes.heading}
						fontWeight={600}
						variant="h6"
						style={labelStyles || {}}
					>
						{getLabel()}
						{showInfoButton && label?.trim() && (
							<InfoButton message={infoMessage} {...infoButtonProps} />
						)}
					</Typography>
					<TextField {...textFieldProps} />
				</>
			)}
		</Box>
	);
};
export default React.memo(InputConfig);
